<template>
  <!-- The signed-in user's corner, on the dark chrome: the credits pill (the way to more credits: /pricing carries the
       contact CTA for a signed-in user) and the avatar with its menu. TopBar and the editor's header share it. -->
  <div class="flex items-center gap-2.5">
    <router-link
      :to="{ name: 'pricing' }"
      class="inline-flex items-center gap-1 px-3 py-[5px] rounded-md bg-white/10 hover:bg-white/15 transition-colors text-white text-[13px] font-medium"
      :title="`${authStore.creditsBalance} credits — add more`"
    >
      <Coins :size="14" aria-hidden="true" />
      {{ authStore.creditsBalance }}
    </router-link>
    <div ref="menuRef" class="relative" @keydown.esc="open = false">
      <button
        type="button"
        class="w-[34px] h-[34px] rounded-full overflow-hidden flex items-center justify-center text-white font-semibold text-[13px] select-none bg-primary ring-offset-2 ring-offset-dark-nav transition focus:ring-2 focus:ring-white/40"
        :title="authStore.user?.email"
        aria-haspopup="menu"
        :aria-expanded="open"
        @click="open = !open"
      >
        <img v-if="authStore.user?.avatar_url" :src="authStore.user.avatar_url" alt="Avatar" class="w-full h-full object-cover" />
        <span v-else>{{ avatarLetter }}</span>
      </button>
      <div
        v-if="open"
        class="absolute right-0 mt-2 w-52 rounded-[10px] bg-surface shadow-lg border border-border overflow-hidden z-20"
        role="menu"
      >
        <div class="px-3 py-2 text-xs text-muted truncate border-b border-border">
          {{ authStore.user?.email }}
        </div>
        <button
          type="button"
          class="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-text hover:bg-bg"
          role="menuitem"
          @click="openAccountSettings(); open = false"
        >
          <Settings :size="15" aria-hidden="true" />
          Account settings
        </button>
        <button
          type="button"
          class="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-text hover:bg-bg border-t border-border"
          role="menuitem"
          @click="logout(); open = false"
        >
          <LogOut :size="15" aria-hidden="true" />
          Log out
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Coins, LogOut, Settings } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useAccountSettingsModal } from '@/composables/useAccountSettingsModal'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { open: openAccountSettings } = useAccountSettingsModal()

const open = ref(false)
const menuRef = ref<HTMLElement | null>(null)

watch(() => route.path, () => (open.value = false))

function onOutsideClick(e: MouseEvent) {
  if (open.value && menuRef.value && !menuRef.value.contains(e.target as Node)) open.value = false
}
onMounted(() => document.addEventListener('click', onOutsideClick))
onBeforeUnmount(() => document.removeEventListener('click', onOutsideClick))

const avatarLetter = computed(() => {
  const first = authStore.user?.first_name?.trim()
  if (first) return first[0].toUpperCase()
  const email = authStore.user?.email
  return email ? email[0].toUpperCase() : '?'
})

function logout() {
  authStore.logout()
  router.push('/login')
}
</script>
